import {
  IServiceEvent,
  ServiceEventPayload,
  ServiceEventId,
  ServiceEventTypes,
  ServiceName,
  IAssetBasedService,
  RegisteredCustomEvent,
  LoadedCustomEvent,
  InternalServiceEventTypeCallback,
  InternalServiceEventMap,
  InternalServiceEventType,
  Logger,
  IABAObservable,
  IServiceErrorEvent,
} from '../types';
import { getLogString } from './utils';

export class AssetBasedService implements IAssetBasedService {
  private observable: IABAObservable | null = null;

  private subscriptions = {} as InternalServiceEventMap;

  constructor(public name: ServiceName, private logger?: Logger) {
    this.handleRegistration = this.handleRegistration.bind(this);
    this.handleObservableNext = this.handleObservableNext.bind(this);
    this.handleObservableError = this.handleObservableError.bind(this);
    this.handleObservableComplete = this.handleObservableComplete.bind(this);
  }

  start() {
    globalThis.addEventListener(
      InternalServiceEventType.Registered,
      this.handleRegistration as EventListener
    );

    this?.logger?.info(`\n\t*Service*: STARTED`);

    this.dispatchLoaded();
  }

  stop() {
    globalThis.removeEventListener(
      InternalServiceEventType.Registered,
      this.handleRegistration as EventListener
    );

    this?.logger?.info(`\n\t*Service*: STOPPED`);
  }

  onRegistered(callback: InternalServiceEventTypeCallback) {
    this.addSubscription(InternalServiceEventType.Registered, callback);
  }

  onInitialize(callback: InternalServiceEventTypeCallback) {
    this.addSubscription(InternalServiceEventType.Initialize, callback);
  }

  onRender(callback: InternalServiceEventTypeCallback) {
    this.addSubscription(InternalServiceEventType.Render, callback);
  }

  onDestroy(callback: InternalServiceEventTypeCallback) {
    this.addSubscription(InternalServiceEventType.Destroy, callback);
  }

  onMessage(callback: InternalServiceEventTypeCallback) {
    this.addSubscription(InternalServiceEventType.Common, callback);
    return () =>
      this.removeSubscription(InternalServiceEventType.Common, callback);
  }

  publish(
    type: ServiceEventTypes,
    id: ServiceEventId,
    payload: ServiceEventPayload
  ) {
    const event = this.createEvent(type, id, payload);

    this.observable?.publish(event);

    this?.logger?.info(getLogString({ id, event, type: type }));
  }

  private handleRegistration(event: RegisteredCustomEvent) {
    // check registered event only for current service
    if (!event || !event.detail || event.detail.name !== this.name) return;

    if (
      !event.detail.observable ||
      typeof event.detail.observable.subscribe !== 'function' ||
      typeof event.detail.observable.publish !== 'function'
    ) {
      this?.logger?.error('\n\tHost shell should provide observable.');

      return;
    }

    // store observable from shell
    this.observable = event.detail.observable;

    this.observable.subscribe({
      next: this.handleObservableNext,
      error: this.handleObservableError,
      complete: this.handleObservableComplete,
    });

    // call onRegistered hook
    this.triggerSubscription(InternalServiceEventType.Registered);
  }

  private handleObservableNext(event: IServiceEvent) {
    // ignore events for other services
    if (event.scope !== this.name) return;

    switch (event.id) {
      // call hook
      case InternalServiceEventType.Initialize:
      case InternalServiceEventType.Render:
      case InternalServiceEventType.Destroy:
        this.triggerSubscription(event.id);
        break;

      // other action types
      default:
        this.triggerSubscription(InternalServiceEventType.Common, event);
        break;
    }
  }

  private handleObservableError(error: IServiceErrorEvent): void {
    this.triggerSubscription(InternalServiceEventType.Common, error);
    // logging for info
    this?.logger?.error('\n\t*Service*: observable "error" was triggered.');
    this?.logger?.error(error);
  }

  private handleObservableComplete(): void {
    // call destroy callback
    this.triggerSubscription(InternalServiceEventType.Destroy);
    // clean up
    this?.logger?.warn('\n\t*Service*: observable "complete" was triggered.');
  }

  private dispatchLoaded() {
    const event: LoadedCustomEvent = new CustomEvent(
      InternalServiceEventType.Loaded,
      {
        detail: { name: this.name },
      }
    );

    globalThis.dispatchEvent(event);

    this?.logger?.info(
      getLogString({
        id: InternalServiceEventType.Loaded,
        event,
      })
    );
  }

  private addSubscription(
    id: InternalServiceEventType,
    callback: InternalServiceEventTypeCallback
  ): void {
    if (this.subscriptions[id]) {
      this.subscriptions[id].push(callback);
    } else {
      this.subscriptions[id] = [callback];
    }

    this?.logger?.info(`\n\t*Service*: Subscribe \`${id}\``);
  }

  private removeSubscription(
    id: InternalServiceEventType,
    callback: InternalServiceEventTypeCallback
  ): void {
    this.subscriptions[id] = this.subscriptions[id].filter(
      subscription => subscription !== callback
    );
    this?.logger?.info(`\n\t*Service*: Unsubscribe \`${id}\``);
  }

  private createEvent(
    type: ServiceEventTypes,
    id: ServiceEventId,
    payload?: ServiceEventPayload
  ): IServiceEvent {
    const baseEvent = { type, scope: this.name, id };

    return { ...baseEvent, ...(payload ? { payload } : {}) };
  }

  private triggerSubscription(
    type: InternalServiceEventType,
    event?: IServiceEvent
  ) {
    if (this.subscriptions.hasOwnProperty(type)) {
      this?.logger?.info(
        getLogString({ initiator: 'Host', id: type, event: event })
      );

      this.subscriptions[type].forEach(subscription => subscription(event));
    }
  }
}
